import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, Trash2, Upload, Image as ImageIcon,
  Shield, AlertTriangle, FileText, Building2, MapPin, X,
  CheckCircle, Info, Loader2, Activity, Zap, Camera, Gauge, Download, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { DatePicker } from '../../components/ui/date-picker';
import {
  PowerQualityStudy, ThermographyStudy, LeakageVoltageStudy,
  UnbalancedCurrentStudy, EarthPitStudy, AssetMaintenanceStudy, PhotoReport,
} from './ElectricalSafetyStudies';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PRIORITIES = [
  { value: 'High',   bg: 'bg-rose-50',  text: 'text-rose-700',   ring: 'ring-rose-200',  dot: 'bg-rose-500' },
  { value: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700',  ring: 'ring-amber-200', dot: 'bg-amber-500' },
  { value: 'Low',    bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-500' },
];

const DEFAULT_SCOPE_ACTIVITIES = [
  'Thermal imaging of electrical panels and feeders',
  'Power quality and harmonics assessment',
  'Leakage voltage measurement (Neutral-to-Earth)',
  'Unbalanced current study',
  'Earth pit resistance measurement',
  'Physical inspection of electrical installation',
  'Review of preventive maintenance records',
];
const DEFAULT_SCOPE_DELIVERABLES = [
  'Electrical safety audit report',
  'Key observations & recommended corrective actions',
  'Photo report of findings',
  'Priority-wise classification of findings',
];

const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('data:') || img.startsWith('http')) return img;
  if (img.startsWith('/api/')) return `${API}${img}`;
  return `${API}${img}`;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ElectricalSafetyAuditForm = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const isEdit = !!reportId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [reportNo, setReportNo] = useState('');

  const [form, setForm] = useState({
    document_details: {
      client: '',
      location: '',
      site_name: '',
      work_order_number: '',
      work_order_date: '',
      date_of_audit_from: '',
      date_of_audit_to: '',
      date_of_submission: '',
      coordinating_person: '',
      audit_performed_by: '',
      report_prepared_by: '',
      report_reviewed_by: '',
      report_approved_by: '',
    },
    site_info: {
      branch_name: '', branch_code: '',
      gross_floor_area_sqft: '', total_floors: '',
      sanctioned_load: '', mdi: '',
      occupancy: '', space_function: '',
    },
    revisions: [{ revision: 'V1.0', date: new Date().toISOString().split('T')[0], description: 'First draft', organiser: '', verified: '', approver: '' }],
    scope_of_work: { activities: [...DEFAULT_SCOPE_ACTIVITIES], deliverables: [...DEFAULT_SCOPE_DELIVERABLES] },
    executive_summary: { introduction: '', methodology: 'On-site audit, instrument-based measurement, document review, and interactions with the facility team.', key_observations: [''] },
    findings: [],
    // Phase 2: Studies
    power_quality: {},
    thermography: {},
    leakage_voltage: {},
    unbalanced_current: {},
    earth_pit: {},
    asset_maintenance: {},
    photo_report: [],
    status: 'draft',
  });

  // ---- Fetchers ----
  const loadReport = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/electrical-safety-audit/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load report');
      const data = await res.json();
      setReportNo(data.report_no || '');
      setForm({
        document_details: data.document_details || form.document_details,
        site_info: data.site_info || form.site_info,
        revisions: (data.revisions && data.revisions.length) ? data.revisions : form.revisions,
        scope_of_work: data.scope_of_work || form.scope_of_work,
        executive_summary: data.executive_summary || form.executive_summary,
        findings: data.findings || [],
        power_quality: data.power_quality || {},
        thermography: data.thermography || {},
        leakage_voltage: data.leakage_voltage || {},
        unbalanced_current: data.unbalanced_current || {},
        earth_pit: data.earth_pit || {},
        asset_maintenance: data.asset_maintenance || {},
        photo_report: data.photo_report || [],
        status: data.status || 'draft',
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const fetchNextNo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/electrical-safety-audit/next-report-no`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setReportNo(d.report_no);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isEdit) loadReport();
    else fetchNextNo();
  }, [isEdit, loadReport, fetchNextNo]);

  // ---- Generic field helpers ----
  const setDoc = (k, v) => setForm(f => ({ ...f, document_details: { ...f.document_details, [k]: v } }));
  const setSite = (k, v) => setForm(f => ({ ...f, site_info: { ...f.site_info, [k]: v } }));
  const setExec = (k, v) => setForm(f => ({ ...f, executive_summary: { ...f.executive_summary, [k]: v } }));

  // ---- Findings ----
  const addFinding = () => {
    setForm(f => ({
      ...f,
      findings: [...f.findings, {
        finding_id: '', location: '', observation: '', possible_impact: '',
        applicable_code: '', recommended_solution: '', priority: 'Medium', photos: [],
      }],
    }));
  };
  const updateFinding = (idx, patch) => {
    setForm(f => ({
      ...f,
      findings: f.findings.map((x, i) => i === idx ? { ...x, ...patch } : x),
    }));
  };
  const removeFinding = (idx) => {
    setForm(f => ({ ...f, findings: f.findings.filter((_, i) => i !== idx) }));
  };
  const addPhotoToFinding = async (idx, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload JPG, PNG, GIF or WEBP only');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setForm(f => ({
        ...f,
        findings: f.findings.map((x, i) => i === idx ? { ...x, photos: [...(x.photos || []), base64] } : x),
      }));
    } catch {
      toast.error('Failed to read image');
    }
  };
  const removePhoto = (fIdx, pIdx) => {
    setForm(f => ({
      ...f,
      findings: f.findings.map((x, i) =>
        i === fIdx ? { ...x, photos: x.photos.filter((_, j) => j !== pIdx) } : x
      ),
    }));
  };

  // ---- Scope helpers ----
  const updateScopeList = (kind, idx, value) => {
    setForm(f => ({
      ...f,
      scope_of_work: {
        ...f.scope_of_work,
        [kind]: f.scope_of_work[kind].map((x, i) => i === idx ? value : x),
      },
    }));
  };
  const addScopeItem = (kind) => {
    setForm(f => ({
      ...f,
      scope_of_work: { ...f.scope_of_work, [kind]: [...f.scope_of_work[kind], ''] },
    }));
  };
  const removeScopeItem = (kind, idx) => {
    setForm(f => ({
      ...f,
      scope_of_work: { ...f.scope_of_work, [kind]: f.scope_of_work[kind].filter((_, i) => i !== idx) },
    }));
  };

  // ---- Key observations ----
  const updateObs = (idx, value) => {
    setForm(f => ({
      ...f,
      executive_summary: {
        ...f.executive_summary,
        key_observations: f.executive_summary.key_observations.map((x, i) => i === idx ? value : x),
      },
    }));
  };

  // ---- Save ----
  const save = async (statusOverride) => {
    if (!form.document_details.client.trim()) {
      toast.error('Please enter Client name');
      setActiveTab('header');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        status: statusOverride || form.status,
      };
      const url = isEdit
        ? `${API}/api/electrical-safety-audit/${reportId}`
        : `${API}/api/electrical-safety-audit`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      const saved = await res.json();
      toast.success(isEdit ? 'Report updated' : 'Report created');
      if (!isEdit) {
        navigate(`/projects/project-reports/audit/electrical-safety/${saved.id}/edit`, { replace: true });
      } else {
        // reload to reflect new image URLs
        loadReport();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Download PDF ----
  const downloadPdf = async () => {
    if (!reportId) {
      toast.error('Please save the report first');
      return;
    }
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/electrical-safety-audit/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ESA_${(reportNo || 'report').replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(false);
    }
  };

  // ---- Clone this report ----
  const cloneReport = async () => {
    if (!reportId) return;
    if (!window.confirm('Clone this report into a new draft? Photos will NOT be copied.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/electrical-safety-audit/${reportId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to clone');
      const cloned = await res.json();
      toast.success(`Cloned to ${cloned.report_no}`);
      navigate(`/projects/project-reports/audit/electrical-safety/${cloned.id}/edit`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Computed summary ----
  const summary = form.findings.reduce((acc, f) => {
    const p = (f.priority || '').toLowerCase();
    if (p === 'high') acc.high += 1;
    else if (p === 'medium') acc.medium += 1;
    else if (p === 'low') acc.low += 1;
    return acc;
  }, { high: 0, medium: 0, low: 0 });
  summary.total = summary.high + summary.medium + summary.low;

  const tabs = [
    { id: 'header', label: 'Header', icon: FileText },
    { id: 'site', label: 'Site Info', icon: Building2 },
    { id: 'scope', label: 'Scope', icon: Shield },
    { id: 'summary', label: 'Exec Summary', icon: Info },
    { id: 'findings', label: `Findings (${form.findings.length})`, icon: AlertTriangle },
    { id: 'power_quality', label: 'Power Quality', icon: Activity },
    { id: 'thermography', label: 'Thermography', icon: Zap },
    { id: 'leakage_voltage', label: 'Leakage Voltage', icon: Gauge },
    { id: 'unbalanced_current', label: 'Unbal. Current', icon: Activity },
    { id: 'earth_pit', label: 'Earth Pit', icon: Shield },
    { id: 'asset_maintenance', label: 'Assets', icon: Building2 },
    { id: 'photo_report', label: 'Photo Report', icon: Camera },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-red-600" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="esa-form-page">
      {/* Header bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/projects/project-reports/audit')}
              className="p-2 hover:bg-slate-100 rounded-lg"
              data-testid="back-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Electrical Safety Audit</h1>
              <p className="text-xs text-slate-500 font-mono">{reportNo || 'New Report'}</p>
            </div>
          </div>

          {/* Summary chip */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 text-rose-700 rounded-full text-xs font-semibold border border-rose-200">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> High {summary.high}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Medium {summary.medium}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Low {summary.low}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => save('draft')}
              disabled={saving}
              data-testid="save-draft-btn"
              className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Save Draft
            </button>
            {isEdit && (
              <button
                onClick={cloneReport}
                data-testid="clone-btn"
                className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
                title="Clone to a new draft (without photos)"
              >
                <Copy size={16} /> Clone
              </button>
            )}
            {isEdit && (
              <button
                onClick={downloadPdf}
                disabled={downloading}
                data-testid="download-pdf-btn"
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                Download PDF
              </button>
            )}
            <button
              onClick={() => save('completed')}
              disabled={saving}
              data-testid="save-complete-btn"
              className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center gap-2 disabled:opacity-50"
            >
              <CheckCircle size={16} /> Mark Complete
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-t border-slate-200 pt-3 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap ${
                  activeTab === t.id ? 'bg-red-50 text-red-700' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        {activeTab === 'header' && (
          <HeaderTab doc={form.document_details} setDoc={setDoc} revisions={form.revisions} setRevisions={r => setForm(f => ({ ...f, revisions: r }))} />
        )}
        {activeTab === 'site' && (
          <SiteTab site={form.site_info} setSite={setSite} />
        )}
        {activeTab === 'scope' && (
          <ScopeTab
            scope={form.scope_of_work}
            updateItem={updateScopeList}
            addItem={addScopeItem}
            removeItem={removeScopeItem}
          />
        )}
        {activeTab === 'summary' && (
          <SummaryTab summary={form.executive_summary} setExec={setExec} updateObs={updateObs}
            addObs={() => setForm(f => ({ ...f, executive_summary: { ...f.executive_summary, key_observations: [...f.executive_summary.key_observations, ''] } }))}
            removeObs={(i) => setForm(f => ({ ...f, executive_summary: { ...f.executive_summary, key_observations: f.executive_summary.key_observations.filter((_, idx) => idx !== i) } }))}
            counts={summary}
          />
        )}
        {activeTab === 'findings' && (
          <FindingsTab
            findings={form.findings}
            addFinding={addFinding}
            updateFinding={updateFinding}
            removeFinding={removeFinding}
            addPhoto={addPhotoToFinding}
            removePhoto={removePhoto}
          />
        )}
        {activeTab === 'power_quality' && (
          <PowerQualityStudy
            value={form.power_quality}
            onChange={(v) => setForm(f => ({ ...f, power_quality: v }))}
          />
        )}
        {activeTab === 'thermography' && (
          <ThermographyStudy
            value={form.thermography}
            onChange={(v) => setForm(f => ({ ...f, thermography: v }))}
          />
        )}
        {activeTab === 'leakage_voltage' && (
          <LeakageVoltageStudy
            value={form.leakage_voltage}
            onChange={(v) => setForm(f => ({ ...f, leakage_voltage: v }))}
          />
        )}
        {activeTab === 'unbalanced_current' && (
          <UnbalancedCurrentStudy
            value={form.unbalanced_current}
            onChange={(v) => setForm(f => ({ ...f, unbalanced_current: v }))}
          />
        )}
        {activeTab === 'earth_pit' && (
          <EarthPitStudy
            value={form.earth_pit}
            onChange={(v) => setForm(f => ({ ...f, earth_pit: v }))}
          />
        )}
        {activeTab === 'asset_maintenance' && (
          <AssetMaintenanceStudy
            value={form.asset_maintenance}
            onChange={(v) => setForm(f => ({ ...f, asset_maintenance: v }))}
          />
        )}
        {activeTab === 'photo_report' && (
          <PhotoReport
            value={form.photo_report}
            onChange={(v) => setForm(f => ({ ...f, photo_report: v }))}
            findings={form.findings}
          />
        )}
      </div>
    </div>
  );
};

// ------------- Sub Tabs (kept small + named) -------------

const Label = ({ children }) => (
  <label className="block text-xs font-medium text-slate-600 mb-1">{children}</label>
);
const Input = (props) => (
  <input {...props} className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent ${props.className || ''}`} />
);
const TextArea = (props) => (
  <textarea {...props} className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent ${props.className || ''}`} />
);

export const HeaderTab = ({ doc, setDoc, revisions, setRevisions }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-base font-semibold text-slate-800 mb-3">Customer & Site</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Client *</Label><Input data-testid="client" value={doc.client || ''} onChange={e => setDoc('client', e.target.value)} placeholder="DB Schenker" /></div>
        <div><Label>Site Name</Label><Input value={doc.site_name || ''} onChange={e => setDoc('site_name', e.target.value)} placeholder="Mannur Phase II" /></div>
        <div className="md:col-span-2"><Label>Location / Address</Label><TextArea rows={2} value={doc.location || ''} onChange={e => setDoc('location', e.target.value)} placeholder="Full site address" /></div>
        <div><Label>Work Order Number</Label><Input value={doc.work_order_number || ''} onChange={e => setDoc('work_order_number', e.target.value)} /></div>
        <div><Label>Work Order Date</Label><DatePicker value={doc.work_order_date || ''} onChange={(v) => setDoc('work_order_date', v)} placeholder="DD-MM-YYYY" /></div>
        <div><Label>Audit Date From</Label><DatePicker value={doc.date_of_audit_from || ''} onChange={(v) => setDoc('date_of_audit_from', v)} placeholder="DD-MM-YYYY" /></div>
        <div><Label>Audit Date To</Label><DatePicker value={doc.date_of_audit_to || ''} onChange={(v) => setDoc('date_of_audit_to', v)} placeholder="DD-MM-YYYY" /></div>
        <div><Label>Date of Submission</Label><DatePicker value={doc.date_of_submission || ''} onChange={(v) => setDoc('date_of_submission', v)} placeholder="DD-MM-YYYY" /></div>
        <div><Label>Coordinating Person</Label><Input value={doc.coordinating_person || ''} onChange={e => setDoc('coordinating_person', e.target.value)} /></div>
      </div>
    </div>
    <div>
      <h2 className="text-base font-semibold text-slate-800 mb-3">Audit Team</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Audit Performed By</Label><Input value={doc.audit_performed_by || ''} onChange={e => setDoc('audit_performed_by', e.target.value)} /></div>
        <div><Label>Report Prepared By</Label><Input value={doc.report_prepared_by || ''} onChange={e => setDoc('report_prepared_by', e.target.value)} /></div>
        <div><Label>Report Reviewed By</Label><Input value={doc.report_reviewed_by || ''} onChange={e => setDoc('report_reviewed_by', e.target.value)} /></div>
        <div><Label>Report Approved By</Label><Input value={doc.report_approved_by || ''} onChange={e => setDoc('report_approved_by', e.target.value)} /></div>
      </div>
    </div>
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-slate-800">Issue & Revision Record</h2>
        <button
          onClick={() => setRevisions([...revisions, { revision: `V${revisions.length + 1}.0`, date: new Date().toISOString().split('T')[0], description: '', organiser: '', verified: '', approver: '' }])}
          className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
        >
          <Plus size={14} /> Add Revision
        </button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Revision</th>
              <th className="px-2 py-2 text-left">Date</th>
              <th className="px-2 py-2 text-left">Description</th>
              <th className="px-2 py-2 text-left">Organiser</th>
              <th className="px-2 py-2 text-left">Verified</th>
              <th className="px-2 py-2 text-left">Approver</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {revisions.map((r, i) => (
              <tr key={i}>
                <td className="p-1"><Input value={r.revision} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, revision: e.target.value } : x))} /></td>
                <td className="p-1"><Input type="date" value={r.date || ''} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} /></td>
                <td className="p-1"><Input value={r.description || ''} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} /></td>
                <td className="p-1"><Input value={r.organiser || ''} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, organiser: e.target.value } : x))} /></td>
                <td className="p-1"><Input value={r.verified || ''} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, verified: e.target.value } : x))} /></td>
                <td className="p-1"><Input value={r.approver || ''} onChange={e => setRevisions(revisions.map((x, idx) => idx === i ? { ...x, approver: e.target.value } : x))} /></td>
                <td className="p-1">
                  {revisions.length > 1 && (
                    <button onClick={() => setRevisions(revisions.filter((_, idx) => idx !== i))} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const SiteTab = ({ site, setSite }) => (
  <div className="space-y-4">
    <h2 className="text-base font-semibold text-slate-800">Site Information</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div><Label>Branch Name</Label><Input value={site.branch_name || ''} onChange={e => setSite('branch_name', e.target.value)} /></div>
      <div><Label>Branch Code</Label><Input value={site.branch_code || ''} onChange={e => setSite('branch_code', e.target.value)} /></div>
      <div><Label>Total Floors</Label><Input type="number" value={site.total_floors || ''} onChange={e => setSite('total_floors', e.target.value)} /></div>
      <div><Label>Gross Floor Area (sqft)</Label><Input type="number" value={site.gross_floor_area_sqft || ''} onChange={e => setSite('gross_floor_area_sqft', e.target.value)} /></div>
      <div><Label>Conditioned Area (sqft)</Label><Input type="number" value={site.conditioned_area_sqft || ''} onChange={e => setSite('conditioned_area_sqft', e.target.value)} /></div>
      <div><Label>Year of Construction</Label><Input value={site.year_of_construction || ''} onChange={e => setSite('year_of_construction', e.target.value)} /></div>
      <div><Label>Last Renovation</Label><Input value={site.last_renovation || ''} onChange={e => setSite('last_renovation', e.target.value)} /></div>
      <div><Label>Sanctioned Load</Label><Input value={site.sanctioned_load || ''} onChange={e => setSite('sanctioned_load', e.target.value)} placeholder="e.g. 250 kVA" /></div>
      <div><Label>MDI</Label><Input value={site.mdi || ''} onChange={e => setSite('mdi', e.target.value)} /></div>
      <div><Label>Occupancy</Label><Input value={site.occupancy || ''} onChange={e => setSite('occupancy', e.target.value)} placeholder="Typical occupancy" /></div>
      <div className="md:col-span-2"><Label>Space Function</Label><Input value={site.space_function || ''} onChange={e => setSite('space_function', e.target.value)} placeholder="Office, warehouse, mechanical, etc." /></div>
    </div>
  </div>
);

const ScopeList = ({ title, kind, items, updateItem, addItem, removeItem }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      <button onClick={() => addItem(kind)} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"><Plus size={14} /> Add</button>
    </div>
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
          <Input value={item} onChange={e => updateItem(kind, i, e.target.value)} placeholder={`${title.slice(0, -1)} description`} />
          <button onClick={() => removeItem(kind, i)} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-slate-400 italic">No items yet — click Add</p>}
    </div>
  </div>
);

const ScopeTab = ({ scope, updateItem, addItem, removeItem }) => (
  <div className="space-y-6">
    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
      <Info size={14} className="inline mr-1" />
      Pre-populated with standard ESA scope. Edit, add or remove items to match this audit.
    </div>
    <ScopeList title="Activities" kind="activities" items={scope.activities} updateItem={updateItem} addItem={addItem} removeItem={removeItem} />
    <ScopeList title="Deliverables" kind="deliverables" items={scope.deliverables} updateItem={updateItem} addItem={addItem} removeItem={removeItem} />
  </div>
);

const SummaryTab = ({ summary, setExec, updateObs, addObs, removeObs, counts }) => (
  <div className="space-y-6">
    {/* Auto count cards */}
    <div className="grid grid-cols-4 gap-3">
      <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-center">
        <p className="text-2xl font-bold text-rose-700">{counts.high}</p>
        <p className="text-xs text-rose-600">High Priority</p>
      </div>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
        <p className="text-2xl font-bold text-amber-700">{counts.medium}</p>
        <p className="text-xs text-amber-600">Medium Priority</p>
      </div>
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
        <p className="text-2xl font-bold text-emerald-700">{counts.low}</p>
        <p className="text-xs text-emerald-600">Low Priority</p>
      </div>
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
        <p className="text-2xl font-bold text-slate-700">{counts.total}</p>
        <p className="text-xs text-slate-600">Total Findings</p>
      </div>
    </div>

    <div>
      <Label>Introduction</Label>
      <TextArea rows={3} value={summary.introduction || ''} onChange={e => setExec('introduction', e.target.value)} placeholder="Briefly describe the purpose and context of this audit." />
    </div>
    <div>
      <Label>Methodology</Label>
      <TextArea rows={2} value={summary.methodology || ''} onChange={e => setExec('methodology', e.target.value)} />
    </div>
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700">Key Observations</h3>
        <button onClick={addObs} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-2">
        {summary.key_observations.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-slate-400 w-6">•</span>
            <Input value={o} onChange={e => updateObs(i, e.target.value)} placeholder="Observation bullet point" />
            <button onClick={() => removeObs(i)} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FindingsTab = ({ findings, addFinding, updateFinding, removeFinding, addPhoto, removePhoto }) => {
  const fileInputs = useRef({});
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Key Findings & Observations</h2>
          <p className="text-xs text-slate-500">Add each finding with location, impact, recommendation, priority, and photos.</p>
        </div>
        <button onClick={addFinding} data-testid="add-finding-btn" className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center gap-2">
          <Plus size={16} /> Add Finding
        </button>
      </div>

      {findings.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-lg">
          <AlertTriangle className="mx-auto text-slate-300 mb-2" size={36} />
          <p className="text-sm text-slate-500">No findings yet</p>
          <p className="text-xs text-slate-400">Click "Add Finding" to record your first observation</p>
        </div>
      )}

      {findings.map((f, idx) => {
        const prio = PRIORITIES.find(p => p.value === f.priority) || PRIORITIES[1];
        return (
          <div key={idx} className={`border-2 rounded-lg p-4 ${prio.ring} ring-1 bg-white`} data-testid={`finding-card-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-slate-100 rounded text-xs font-mono font-bold text-slate-700">#{idx + 1}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${prio.bg} ${prio.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                  {f.priority}
                </span>
              </div>
              <button onClick={() => removeFinding(idx)} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Location *</Label><Input value={f.location} onChange={e => updateFinding(idx, { location: e.target.value })} placeholder="e.g. Main LT Panel" /></div>
              <div>
                <Label>Priority</Label>
                <select value={f.priority} onChange={e => updateFinding(idx, { priority: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.value}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><Label>Observation *</Label><TextArea rows={2} value={f.observation} onChange={e => updateFinding(idx, { observation: e.target.value })} placeholder="What was found during the audit" /></div>
              <div><Label>Possible Impact</Label><TextArea rows={2} value={f.possible_impact} onChange={e => updateFinding(idx, { possible_impact: e.target.value })} placeholder="e.g. Electrical fire hazard, risk of explosion" /></div>
              <div><Label>Applicable Code / Standard</Label><Input value={f.applicable_code} onChange={e => updateFinding(idx, { applicable_code: e.target.value })} placeholder="e.g. NETA, CEA 2010, NFPA 70E" /></div>
              <div className="md:col-span-2"><Label>Recommended Solution</Label><TextArea rows={2} value={f.recommended_solution} onChange={e => updateFinding(idx, { recommended_solution: e.target.value })} placeholder="Specific corrective action" /></div>
            </div>

            {/* Photos */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <Label>Photos ({(f.photos || []).length})</Label>
                <button onClick={() => fileInputs.current[idx]?.click()} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                  <Upload size={14} /> Upload Photo
                </button>
                <input
                  ref={el => (fileInputs.current[idx] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) addPhoto(idx, file);
                    e.target.value = '';
                  }}
                />
              </div>
              {(f.photos || []).length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {f.photos.map((p, pIdx) => (
                    <div key={pIdx} className="relative group">
                      <img src={getImageSrc(p)} alt={`Finding ${idx + 1} #${pIdx + 1}`} className="w-full h-20 object-cover rounded border border-slate-200" />
                      <button
                        onClick={() => removePhoto(idx, pIdx)}
                        className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {findings.length > 0 && (
        <button onClick={addFinding} className="w-full py-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-red-300 hover:text-red-600 transition-colors text-sm flex items-center justify-center gap-2">
          <Plus size={16} /> Add Another Finding
        </button>
      )}
    </div>
  );
};

export default ElectricalSafetyAuditForm;
